import type {
  ColumnFiltersState,
  ColumnDef,
  Row,
  Table as TanstackTable,
} from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import * as React from "react";

import { DataTable } from "@/components/data-table/data-table";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { DataTableSortList } from "@/components/data-table/data-table-sort-list";
import { DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useDataTable } from "@/hooks/use-data-table";
import type { ExtendedColumnSort } from "@/types/data-table";

const DATE_SORT_SCORE_FALLBACK = 10_000;

function normalizeSortKey(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function getDateSortScore(columnKey: string): number {
  const normalized = normalizeSortKey(columnKey);

  // Prefer explicit order date fields first.
  if (normalized === "orderdate" || normalized.includes("orderdate")) return 0;
  if (normalized === "dateofsale" || normalized === "orderedat") return 5;
  if (normalized === "lastorderat" || normalized === "ordercreatedat") return 8;

  // Then created timestamp fields.
  if (normalized === "createdat" || normalized.endsWith("createdat")) return 10;
  if (normalized === "creationtime" || normalized.endsWith("creationtime")) return 11;
  if (normalized === "_creationtime") return 12;

  // Then any date/time-like field.
  if (normalized.includes("date")) return 20;
  if (normalized.endsWith("at")) return 30;
  if (normalized.includes("timestamp") || normalized.includes("time")) return 35;

  return DATE_SORT_SCORE_FALLBACK;
}

function resolveGlobalDefaultSorting<TData>(
  columns: ColumnDef<TData>[],
): ExtendedColumnSort<TData>[] | undefined {
  let best: { id: string; score: number } | null = null;

  for (const column of columns) {
    const key =
      (typeof column.id === "string" ? column.id : undefined) ??
      (typeof (column as { accessorKey?: unknown }).accessorKey === "string"
        ? ((column as { accessorKey?: string }).accessorKey as string)
        : undefined);
    if (!key) continue;
    if (key.startsWith("_")) continue;

    const score = getDateSortScore(key);
    if (score >= DATE_SORT_SCORE_FALLBACK) continue;

    if (!best || score < best.score) {
      best = { id: key, score };
    }
  }

  if (!best) return undefined;
  return [{ id: best.id as any, desc: true }];
}

interface InternalEntityDataTableSelectionContext<TData> {
  selectedRows: TData[];
  selectedIds: string[];
  table: TanstackTable<TData>;
  clearSelection: () => void;
}

interface InternalEntityDataTableProps<TData extends { id?: string; _id?: string }> {
  data: TData[];
  columns: ColumnDef<TData>[];
  pageSize?: number;
  initialSorting?: ExtendedColumnSort<TData>[];
  initialColumnFilters?: ColumnFiltersState;
  enableSelection?: boolean;
  actionBar?: (
    context: InternalEntityDataTableSelectionContext<TData>,
  ) => React.ReactNode;
  onSelectionChange?: (
    context: Omit<InternalEntityDataTableSelectionContext<TData>, "clearSelection">,
  ) => void;
  /** Called when the visible page rows change (pagination, sort, filter). */
  onVisibleRowsChange?: (visibleIds: string[]) => void;
  onRowClick?: (row: Row<TData>) => void;
  expandedRow?: {
    render: React.ComponentType<{ row: Row<TData> }>;
    getCanExpand?: (row: TData) => boolean;
  };
  getRowClassName?: (row: Row<TData>) => string | undefined;
}

export function InternalEntityDataTable<TData extends { id?: string; _id?: string }>({
  data,
  columns,
  pageSize = 10,
  initialSorting,
  initialColumnFilters,
  enableSelection = true,
  actionBar,
  onSelectionChange,
  onVisibleRowsChange,
  onRowClick,
  expandedRow,
  getRowClassName,
}: InternalEntityDataTableProps<TData>) {
  const expandColumn = React.useMemo<ColumnDef<TData>>(
    () => ({
      id: "_expand",
      header: () => null,
      cell: ({ row }) =>
        row.getCanExpand() ? (
          <Button
            aria-label={row.getIsExpanded() ? "Collapse row" : "Expand row"}
            variant="ghost"
            size="icon-xs"
            onClick={() => row.toggleExpanded()}
          >
            {row.getIsExpanded() ? <ChevronDown /> : <ChevronRight />}
          </Button>
        ) : null,
      size: 36,
      minSize: 36,
      maxSize: 36,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
    }),
    [],
  );

  const selectionColumn = React.useMemo<ColumnDef<TData>>(
    () => ({
      id: "_select",
      header: ({ table }) => (
        <Checkbox
          aria-label="Select all"
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          aria-label="Select row"
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
        />
      ),
      size: 36,
      minSize: 36,
      maxSize: 36,
      enableHiding: false,
      enableSorting: false,
      enableColumnFilter: false,
    }),
    [],
  );

  const tableColumns = React.useMemo(
    () => [
      ...(expandedRow ? [expandColumn] : []),
      ...(enableSelection ? [selectionColumn] : []),
      ...columns,
    ],
    [columns, enableSelection, expandedRow, expandColumn, selectionColumn],
  );

  const resolvedInitialSorting = React.useMemo(() => {
    if (initialSorting && initialSorting.length > 0) return initialSorting;

    const dateSorting = resolveGlobalDefaultSorting(columns);
    if (dateSorting && dateSorting.length > 0) return dateSorting;

    return undefined;
  }, [columns, initialSorting]);

  const initialState = React.useMemo(
    () => ({
      pagination: { pageIndex: 0, pageSize },
      sorting: resolvedInitialSorting,
      columnFilters: initialColumnFilters,
    }),
    [initialColumnFilters, resolvedInitialSorting, pageSize],
  );

  const { table } = useDataTable({
    data,
    columns: tableColumns,
    pageCount: Math.max(1, Math.ceil(data.length / pageSize)),
    getRowId: (row, index) => row.id ?? row._id ?? String(index),
    initialState,
    enableExpanding: !!expandedRow,
    getRowCanExpand: expandedRow
      ? (row) => (expandedRow.getCanExpand?.(row.original) ?? true)
      : undefined,
  });

  const selectedRows = table
    .getFilteredSelectedRowModel()
    .rows.map((row) => row.original);
  const selectedIds = table
    .getFilteredSelectedRowModel()
    .rows.map((row) => row.original.id ?? row.original._id ?? row.id);
  const clearSelection = React.useCallback(() => {
    table.resetRowSelection();
  }, [table]);

  const rowSelection = table.getState().rowSelection;
  const selectionSignature = React.useMemo(() => {
    return Object.entries(rowSelection)
      .filter(([, selected]) => selected)
      .map(([id]) => id)
      .sort()
      .join(",");
  }, [rowSelection]);

  React.useEffect(() => {
    if (!onSelectionChange) return;
    void selectionSignature;
    const rows = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original);
    const ids = table
      .getFilteredSelectedRowModel()
      .rows.map((row) => row.original.id ?? row.original._id ?? row.id);
    onSelectionChange({ selectedRows: rows, selectedIds: ids, table });
  }, [onSelectionChange, table, selectionSignature]);

  // Notify parent of visible page row IDs for lazy enrichment.
  // Derive a stable key from pagination + sorting + filters + data length so
  // we only recompute when the visible page actually changes.
  const paginationState = table.getState().pagination;
  const sortingState = table.getState().sorting;
  const filterState = table.getState().columnFilters;
  const visibleRowIds = React.useMemo(() => {
    void paginationState;
    void sortingState;
    void filterState;
    void data.length;
    return table
      .getRowModel()
      .rows.map((row) => row.original.id ?? row.original._id ?? row.id)
      .filter(Boolean) as string[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, paginationState, sortingState, filterState, data.length]);

  React.useEffect(() => {
    onVisibleRowsChange?.(visibleRowIds);
  }, [onVisibleRowsChange, visibleRowIds]);

  const resolvedActionBar =
    enableSelection && selectedRows.length > 0
      ? (actionBar?.({ selectedRows, selectedIds, table, clearSelection }) ?? (
          <DataTableFloatingBar
            selectedCount={selectedRows.length}
            onClear={clearSelection}
          />
        ))
      : undefined;

  return (
    <DataTable
      table={table}
      actionBar={resolvedActionBar}
      ExpandedRow={expandedRow?.render}
      getRowClassName={getRowClassName}
      onRowClick={onRowClick}
    >
      <DataTableToolbar table={table}>
        <DataTableSortList table={table} />
      </DataTableToolbar>
    </DataTable>
  );
}
